import { Zap, BarChart2, FileDown, FileSpreadsheet, Link2, Smartphone } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '../../hooks/useScrollReveal'

const featureIcons = [
  <Zap size={22} className="text-green-600" />,
  <BarChart2 size={22} className="text-green-600" />,
  <FileDown size={22} className="text-green-600" />,
  <FileSpreadsheet size={22} className="text-green-600" />,
  <Link2 size={22} className="text-green-600" />,
  <Smartphone size={22} className="text-green-600" />,
]

const cardDelays = ["delay-100", "delay-200", "delay-300", "delay-100", "delay-200", "delay-300"]

interface Feature {
  title: string
  description: string
}

export default function FeaturesSection() {
  const { t, i18n } = useTranslation()
  const headerRef = useScrollReveal<HTMLDivElement>('features-header', i18n.language)
  const gridRef = useScrollReveal<HTMLDivElement>('features-grid', i18n.language, { threshold: 0.06 })
  const footerRef = useScrollReveal<HTMLDivElement>('features-footer', i18n.language)

  const items = t('landing.features.items', { returnObjects: true }) as Feature[]

  return (
    <section id="features" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-up text-center mb-14">
          <p
            className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.features.sectionLabel')}
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.features.headline')}
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
          {items.map((feature, i) => (
            <div
              key={feature.title}
              className={`reveal-scale ${cardDelays[i]} flex gap-4`}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
                {featureIcons[i]}
              </div>
              <div>
                <h3
                  className="text-base font-bold text-gray-900 dark:text-white mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div ref={footerRef} className="reveal-up delay-200 text-center mt-16 pt-8 border-t border-gray-100 dark:border-gray-700">
          <p
            className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.features.bottomLabel')}
          </p>
        </div>
      </div>
    </section>
  )
}
